import {Trans} from '@lingui/macro';
import {isString} from 'lodash';
import React, {useEffect, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {Paragraph} from '../../../components/common/paragraph';
import {CalendarWeekdayToShortName, getSortedWeekdayNames} from '../../../helpers/calendarHelpers';
import {findTimeZone, TimeFormatsEnum} from '../../../helpers/dateTimeHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {BusinessHour, BusinessHours} from '../../../models/api/businessHoursModel';
import {CalendarWeekDaysEnum, CalendarWeekDaysShortEnum} from '../../../models/api/calendarCommonTypes';
import {TimezoneParameter} from '../../../settings/rules/ruleEditor/parameters/timezoneParameter';
import {getHasBusinessHoursInbox} from '../../../store/featureFlags/featureFlagsSelectors';
import {hasAnyBusinessHour} from '../../helpers/businessHoursHelpers';
import {BusinessHoursDialogWeekdayButton} from './businessHoursDialogWeekdayButton';
import {BusinessHoursDialogWeekdayHours} from './businessHoursDialogWeekdayHours';
import {defaultBusinessHours} from './helpers/common';

/*
  Props.
*/

interface BusinessHoursContentProps {
  businessHours: BusinessHours;
  calendarWeekStartDay: CalendarWeekDaysEnum;
  timeFormat: TimeFormatsEnum;
  onDisable: Handler;
  onUpdate: HandlerOf<BusinessHours>;
  hasUpdateError?: boolean;
}

/*
  Style.
*/

const StyledHeadlineDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.semibold};
  margin-bottom: 6px;
`;

const StyledDaysDiv = styled.div`
  display: flex;
  justify-content: space-between;
  width: 270px;
  margin-bottom: 10px;
  font-weight: ${fontWeights.semibold};
`;

const StyledHoursDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const StyledClearButton = styled(Button)`
  display: inline-block;
  margin-top: 20px;
  margin-left: -12px;
`;

const StyledErrorParagraph = styled(Paragraph)`
  color: ${(p) => p.theme.palette.red.shade40};
  margin-bottom: 20px;
`;

const StyledTimezoneSelectorDiv = styled.div`
  width: 250px;
`;

/*
  Component.
*/

export const BusinessHoursContent = (props: BusinessHoursContentProps) => {
  const {calendarWeekStartDay, businessHours, timeFormat, onDisable, hasUpdateError, onUpdate} = props;

  // Timezone should be set but fall back to default if not.
  const tz = businessHours.tz ?? defaultTimezone;

  const [currentBusinessHours, setCurrentBusinessHours] = useState(businessHours);
  const [previousBusinessHours, setPreviousBusinessHours] = useState(businessHours);
  const [timezone, setTimezone] = useState(tz);

  const hasBusinessHoursInboxFeatureFlag = useAppSelector(getHasBusinessHoursInbox);

  const weekdays = getSortedWeekdayNames(calendarWeekStartDay);
  const hasBusinessHours = hasAnyBusinessHour(businessHours);

  // Used to reset business hours from outside the component.
  useEffect(() => {
    setCurrentBusinessHours(businessHours);
    setTimezone(businessHours.tz ?? defaultTimezone);
  }, [businessHours]);

  const onWeekdayClick = (weekday: CalendarWeekDaysShortEnum) => {
    if (!currentBusinessHours[weekday]) {
      const newBusinessHours = {
        ...currentBusinessHours,
        [weekday]: previousBusinessHours[weekday] ?? defaultBusinessHours,
      };
      onUpdate(newBusinessHours);
      setCurrentBusinessHours(newBusinessHours);
      return;
    }

    const newBusinessHours = {...currentBusinessHours, [weekday]: null};
    onUpdate(newBusinessHours);
    setCurrentBusinessHours(newBusinessHours);
    setPreviousBusinessHours({...previousBusinessHours, [weekday]: currentBusinessHours[weekday]});
  };

  const onWeekdayHoursChange = (weekday: CalendarWeekDaysShortEnum, hours: BusinessHour) => {
    const newBusinessHours = {...currentBusinessHours, [weekday]: hours};
    onUpdate(newBusinessHours);
    setCurrentBusinessHours(newBusinessHours);
  };

  const onTimezoneChange = (newTimezone: string) => {
    const newBusinessHours = {
      ...currentBusinessHours,
      tz: newTimezone,
    };
    onUpdate(newBusinessHours);
    setCurrentBusinessHours(newBusinessHours);
    setTimezone(newTimezone);
  };

  const renderWeekDay = (weekday: CalendarWeekDaysEnum) => {
    const hours = currentBusinessHours[CalendarWeekdayToShortName[weekday]];
    if (!hours) {
      return null;
    }

    return (
      <BusinessHoursDialogWeekdayHours
        key={weekday}
        hours={hours}
        weekday={weekday}
        timeFormat={timeFormat}
        onSelect={onWeekdayHoursChange}
      />
    );
  };

  return (
    <div>
      {hasUpdateError ? (
        <StyledErrorParagraph>
          <Trans id="business-hours-error">
            Something went wrong saving your business hours. Please try again.
          </Trans>
        </StyledErrorParagraph>
      ) : null}
      <StyledHeadlineDiv>
        <Trans id="business-hours-days-headline">Days</Trans>
      </StyledHeadlineDiv>
      <StyledDaysDiv>
        {weekdays.map((weekday) => (
          <BusinessHoursDialogWeekdayButton
            key={weekday}
            active={Boolean(currentBusinessHours[CalendarWeekdayToShortName[weekday]])}
            onClick={onWeekdayClick}
            weekday={CalendarWeekdayToShortName[weekday]}
          />
        ))}
      </StyledDaysDiv>
      <StyledHoursDiv>{weekdays.map(renderWeekDay)}</StyledHoursDiv>

      {hasBusinessHours && hasBusinessHoursInboxFeatureFlag ? (
        <StyledTimezoneSelectorDiv>
          <StyledHeadlineDiv>
            <Trans id="business-hours-timezone-headline">Time zone</Trans>
          </StyledHeadlineDiv>
          <TimezoneParameter selectedItem={timezone} onChange={(t) => isString(t) && onTimezoneChange(t)} />
        </StyledTimezoneSelectorDiv>
      ) : null}

      {hasBusinessHours && !hasBusinessHoursInboxFeatureFlag ? (
        <StyledHeadlineDiv>
          <Trans id="timezone">Timezone:</Trans> {timezone}
        </StyledHeadlineDiv>
      ) : null}

      {currentBusinessHours.enabled && hasBusinessHours ? (
        <StyledClearButton
          onClick={() => onDisable()}
          size={VisualSizesEnum.SMALL}
          variant={ButtonVariantsEnum.LINK}
          interactionId="disable-business-hours"
        >
          <Trans id="clear-business-hours">Clear business hours</Trans>
        </StyledClearButton>
      ) : null}
    </div>
  );
};

/*
 * Helpers.
 */

const defaultTimezone = findTimeZone();
